<template>
  <div class="kpis">
    <div class="kpi">
      <div class="value">
        <package-status-icon :status="props.deal.state" />
        {{ capitalize(props.deal.status) }}
      </div>
      <div class="label">{{ $t('names.status') }}</div>
    </div>
    <div
      v-if="isSupplyOrganization && !props.deal.isNGOrPreferredDeal"
      class="kpi">
      <div class="value">
        {{ capitalize(props.deal.isFlexible ? $t('states.buyerDefinable') : $t('states.predefined')) }}
      </div>
      <div class="label">{{ $t('names.customization') }}</div>
    </div>
    <div
      v-if="!props.deal.purchaseTypeOverridable"
      class="kpi">
      <div class="value">
        {{ purchaseTypeName }}
      </div>
      <div class="label">{{ $t('names.purchaseType') }}</div>
    </div>
    <div
      v-if="isSupplyOrganization && props.deal.state === 'D'"
      class="kpi">
      <div class="value">
        <span class="badge badge-danger">
          {{ capitalize($t('messages.notPublished')) }}
        </span>
      </div>
      <div class="label">{{ $t('names.publishing') }}</div>
    </div>
    <div
      v-if="organizationSettings?.uiEnableDirectBilling"
      class="kpi">
      <div class="value">
        <span
          :class="{ 'badge badge-success': props.deal.directBilling, 'badge badge-danger': !props.deal.directBilling }">
          {{ props.deal.directBillingDescription }}
        </span>
      </div>
      <div class="label">{{ $t('messages.directBilling') }}</div>
    </div>
    <div
      v-if="props.deal.revision > 0 || props.deal.revisedByID != null"
      class="kpi">
      <div class="value">
        <span class="badge badge-info"> rev {{ props.deal.revision }}</span>
      </div>
      <div class="label">{{ $t('names.revision') }}</div>
    </div>
    <div
      v-if="!props.deal.isCuratedDeal && organizationSettings?.bidRequestUnitInformation['can_hide']"
      class="kpi">
      <div class="value">
        <span
          v-if="props.deal.hideUnitInformationInBidRequests"
          class="badge badge-info"
          >{{ $t('messages.willHideScreenInformationInBidRequests') }}</span
        >
        <span
          v-else
          class="badge badge-danger"
          >{{ $t('messages.willShowScreenInformationInBidRequests') }}</span
        >
      </div>
      <div class="label">{{ $t('names.bidRequest', { n: 2 }) }}</div>
    </div>
    <template v-if="showPackageBiddingMetrics">
      <div
        v-if="props.deal.isBiddingMetricsVisible"
        class="kpi">
        <div class="value">
          <span
            :class="{
              'badge badge-success': props.deal.bidRequestSentInTheLastHour,
              'badge badge-danger': !props.deal.bidRequestSentInTheLastHour,
            }">
            {{ props.deal.bidRequestSentInTheLastHourDescription }}
          </span>
        </div>
        <div class="label">{{ $t('messages.bidRequestSentInTheLastHour') }}</div>
      </div>
      <div
        v-if="props.deal.isBiddingMetricsVisible"
        class="kpi">
        <div class="value">
          <span
            :class="{
              'badge badge-success': props.deal.bidResponseInTheLastHour,
              'badge badge-danger': !props.deal.bidResponseInTheLastHour,
            }">
            {{ props.deal.bidResponseInTheLastHourDescription }}
          </span>
        </div>
        <div class="label">{{ $t('messages.bidResponseInTheLastHour') }}</div>
      </div>
      <div
        v-if="!props.deal.isBiddingMetricsVisible && !props.deal.isCuratedDeal"
        class="kpi">
        <div class="value">
          <span
            :class="{
              'badge badge-success': props.deal.didPlayedInTheLastHour,
              'badge badge-danger': !props.deal.didPlayedInTheLastHour,
            }">
            {{ props.deal.didPlayedInTheLastHourDescription }}
          </span>
        </div>
        <div class="label">{{ $t('messages.playedInLastHour') }}</div>
      </div>
    </template>

    <div class="kpi">
      <div class="value">
        {{ props.deal.modifiedOnFormatted }}
      </div>
      <div class="label">{{ $t('messages.lastModifiedOn') }}</div>
    </div>
    <div
      v-if="planName"
      class="kpi">
      <div class="value">
        <router-link
          :to="`/plans/${props.deal.planID}`"
          class="entity-link"
          :title="planName">
          {{ planName }}
        </router-link>
      </div>
      <div class="label">{{ $t('messages.createdFromPlan') }}</div>
    </div>
  </div>
</template>
<script setup lang="ts">
  import { capitalize } from 'vue';

  import PackageStatusIcon from '@/common/components/deals/PackageStatusIcon.vue';
  import { MembershipSettings, Package } from '@/common/models';

  interface IDealDetailsKpisProps {
    deal: Package;
    purchaseTypeName: string;
    isNGExternalOrPreferredExternalDeal: boolean;
    isSupplyOrganization: boolean;
    organizationSettings: MembershipSettings;
    planName?: string;
    showPackageBiddingMetrics: boolean;
  }

  const props = defineProps<IDealDetailsKpisProps>();
</script>
